import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User as UserIcon, UserPlus, UserCheck, MessageSquare, Ban, Flag, X, CheckCircle } from 'lucide-react';
import { blockUser, submitReport, followUser, unfollowUser, getPublicProfile, type ReportReason } from '../api/client';
import { colors } from '../constants/colors';
import { UserAvatar } from './UserAvatar';

interface Props {
  sellerId: string;
  sellerUsername: string;
  sellerAvatarUrl?: string | null;
  currentUserId?: string | null;
  onRequireAuth?: () => void;
}

const REASONS: { value: ReportReason; label: string }[] = [
  { value: 'SPAM', label: 'Spam or scam' },
  { value: 'HARASSMENT', label: 'Harassment or bullying' },
  { value: 'HATE_SPEECH', label: 'Hate speech or symbols' },
  { value: 'EXPLICIT_CONTENT', label: 'Nudity or sexual content' },
  { value: 'VIOLENCE', label: 'Violence or dangerous behavior' },
  { value: 'FRAUD', label: 'Fraudulent listing or transaction' },
  { value: 'PROHIBITED_ITEM', label: 'Prohibited or illegal item' },
  { value: 'IMPERSONATION', label: 'Impersonation or fake account' },
  { value: 'OTHER', label: 'Other' },
];

export function SellerMenu({ sellerId, sellerUsername, sellerAvatarUrl, currentUserId, onRequireAuth }: Props) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  const [isFollowing, setIsFollowing] = useState<boolean | null>(null);
  const [followBusy, setFollowBusy] = useState(false);

  const isSelf = currentUserId === sellerId;

  useEffect(() => {
    if (!open || isSelf || !currentUserId) return;
    getPublicProfile(sellerUsername)
      .then(profile => setIsFollowing(profile.isFollowing ?? false))
      .catch(() => {});
  }, [open, sellerUsername, isSelf, currentUserId]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  const requireAuth = (): boolean => {
    if (!currentUserId) {
      if (onRequireAuth) onRequireAuth();
      return false;
    }
    return true;
  };

  const handleFollow = async () => {
    setOpen(false);
    if (!requireAuth()) return;
    if (followBusy) return;
    setFollowBusy(true);
    try {
      if (isFollowing) {
        await unfollowUser(sellerId);
        setIsFollowing(false);
        setToast(`Unfollowed @${sellerUsername}`);
      } else {
        await followUser(sellerId);
        setIsFollowing(true);
        setToast(`Following @${sellerUsername}`);
      }
    } catch {
      setToast('Something went wrong');
    } finally {
      setFollowBusy(false);
    }
  };

  const handleViewProfile = () => {
    setOpen(false);
    navigate(`/user/${sellerUsername}`);
  };

  const handleMessage = () => {
    setOpen(false);
    if (!requireAuth()) return;
    const params = new URLSearchParams({ recipientId: sellerId, recipientName: sellerUsername });
    navigate(`/inbox?${params.toString()}`);
  };

  const handleBlockClick = () => {
    setOpen(false);
    if (!requireAuth()) return;
    setShowBlockConfirm(true);
  };

  const handleReportClick = () => {
    setOpen(false);
    if (!requireAuth()) return;
    setShowReport(true);
  };

  return (
    <>
      <div ref={wrapRef} style={{ position: 'relative' }}>
        <button
          type="button"
          onClick={() => setOpen(v => !v)}
          className="bv-seller-bar"
          style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}
          aria-haspopup="menu"
          aria-expanded={open}
        >
          <UserAvatar src={sellerAvatarUrl} alt={sellerUsername ?? 'Seller'} size={36} />
          <span className="bv-seller-name">{sellerUsername}</span>
        </button>

        {open && (
          <div
            role="menu"
            style={{
              position: 'absolute',
              top: 'calc(100% + 6px)',
              left: 0,
              minWidth: 180,
              background: '#fff',
              borderRadius: 10,
              boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
              border: `1px solid ${colors.gray[200]}`,
              overflow: 'hidden',
              zIndex: 50,
            }}
          >
            {!isSelf && <MenuItem icon={isFollowing ? <UserCheck size={15} /> : <UserPlus size={15} />} label={isFollowing ? 'Unfollow' : 'Follow'} onClick={handleFollow} />}
            <MenuItem icon={<UserIcon size={15} />} label="View Profile" onClick={handleViewProfile} />
            {!isSelf && <MenuItem icon={<MessageSquare size={15} />} label="Message" onClick={handleMessage} />}
            {!isSelf && <MenuItem icon={<Ban size={15} />} label="Block" onClick={handleBlockClick} danger />}
            {!isSelf && <MenuItem icon={<Flag size={15} />} label="Report" onClick={handleReportClick} danger />}
          </div>
        )}
      </div>

      {showReport && (
        <ReportModal
          sellerId={sellerId}
          sellerUsername={sellerUsername}
          onClose={() => setShowReport(false)}
          onDone={() => { setShowReport(false); setToast('Report submitted — thanks'); }}
        />
      )}

      {showBlockConfirm && (
        <BlockConfirmModal
          sellerUsername={sellerUsername}
          onClose={() => setShowBlockConfirm(false)}
          onConfirm={async () => {
            try {
              await blockUser(sellerId);
              setShowBlockConfirm(false);
              setToast(`Blocked @${sellerUsername}`);
            } catch (err) {
              console.error('Failed to block user:', err);
              setShowBlockConfirm(false);
              setToast('Failed to block user');
            }
          }}
        />
      )}

      {toast && (
        <div
          role="status"
          style={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            background: '#111',
            color: '#fff',
            padding: '10px 16px',
            borderRadius: 10,
            fontSize: 13,
            fontWeight: 500,
            zIndex: 2000,
            boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <CheckCircle size={14} color="#22C55E" />
          {toast}
        </div>
      )}
    </>
  );
}

function MenuItem({ icon, label, onClick, danger }: { icon: React.ReactNode; label: string; onClick: () => void; danger?: boolean }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      onMouseEnter={e => (e.currentTarget.style.background = colors.gray[100])}
      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: '100%',
        padding: '10px 14px',
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        fontSize: 13,
        color: danger ? '#DC2626' : '#111',
        textAlign: 'left',
      }}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

function BlockConfirmModal({ sellerUsername, onClose, onConfirm }: { sellerUsername: string; onClose: () => void; onConfirm: () => void | Promise<void> }) {
  const [busy, setBusy] = useState(false);
  return (
    <ModalShell onClose={onClose} title={`Block @${sellerUsername}?`}>
      <p style={{ margin: '0 0 16px', fontSize: 14, color: colors.gray[600], lineHeight: 1.5 }}>
        You won't see their messages or shows, and they won't be able to message you.
      </p>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button onClick={onClose} disabled={busy} style={secondaryBtn}>Cancel</button>
        <button
          onClick={async () => { setBusy(true); await onConfirm(); setBusy(false); }}
          disabled={busy}
          style={{ ...dangerBtn, opacity: busy ? 0.7 : 1 }}
        >
          {busy ? 'Blocking...' : 'Block'}
        </button>
      </div>
    </ModalShell>
  );
}

function ReportModal({ sellerId, sellerUsername, onClose, onDone }: { sellerId: string; sellerUsername: string; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [detail, setDetail] = useState('');
  const [busy, setBusy] = useState(false);

  const canSubmit = reason != null && (reason !== 'OTHER' || detail.trim().length > 0) && !busy;

  const handleSubmit = async () => {
    if (!canSubmit || !reason) return;
    setBusy(true);
    try {
      await submitReport({
        targetType: 'USER',
        targetId: sellerId,
        reason,
        detail: detail.trim() || undefined,
      });
      onDone();
    } catch (err) {
      console.error('Failed to submit report:', err);
      setBusy(false);
    }
  };

  return (
    <ModalShell onClose={onClose} title={`Report @${sellerUsername}`}>
      <div style={{ maxHeight: 320, overflowY: 'auto', marginBottom: 12 }}>
        {REASONS.map(r => (
          <label
            key={r.value}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 4px',
              borderBottom: `1px solid ${colors.gray[100]}`,
              cursor: 'pointer',
              fontSize: 14,
            }}
          >
            <input
              type="radio"
              name="reportReason"
              checked={reason === r.value}
              onChange={() => setReason(r.value)}
              style={{ accentColor: colors.brand.blue }}
            />
            {r.label}
          </label>
        ))}
      </div>
      {reason && (
        <textarea
          placeholder={reason === 'OTHER' ? 'Please provide details (required)' : 'Add details (optional)'}
          value={detail}
          onChange={e => setDetail(e.target.value)}
          maxLength={500}
          rows={3}
          style={{
            width: '100%',
            padding: 10,
            borderRadius: 8,
            border: `1px solid ${colors.gray[200]}`,
            fontSize: 13,
            resize: 'vertical',
            fontFamily: 'inherit',
            marginBottom: 12,
          }}
        />
      )}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button onClick={onClose} disabled={busy} style={secondaryBtn}>Cancel</button>
        <button onClick={handleSubmit} disabled={!canSubmit} style={{ ...dangerBtn, opacity: canSubmit ? 1 : 0.5 }}>
          {busy ? 'Submitting...' : 'Submit Report'}
        </button>
      </div>
    </ModalShell>
  );
}

function ModalShell({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1500,
        padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 14,
          padding: 20,
          width: '100%',
          maxWidth: 420,
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>{title}</h3>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.gray[600], padding: 4, display: 'flex' }}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

const secondaryBtn: React.CSSProperties = {
  padding: '8px 14px',
  borderRadius: 8,
  border: `1px solid ${colors.gray[200]}`,
  background: '#fff',
  color: '#111',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

const dangerBtn: React.CSSProperties = {
  padding: '8px 14px',
  borderRadius: 8,
  border: 'none',
  background: '#DC2626',
  color: '#fff',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};
